import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadBlob, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GraduationCap, CheckCircle2, Clock, ExternalLink, Award, FileDown, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import ResourceEmbeds from "@/components/ResourceEmbeds";
import QuizDialog from "@/components/QuizDialog";
import ReadinessAnalytics from "@/components/ReadinessAnalytics";
import ListenButton from "@/components/ListenButton";
import { useAuth } from "@/context/AuthContext";

export default function Readiness() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [meta, setMeta] = useState({ certified: false, passed: 0, pass_mark: 70 });
  const [quizModule, setQuizModule] = useState(null);
  const [certBusy, setCertBusy] = useState(false);

  const downloadCertificate = async () => {
    setCertBusy(true);
    try {
      await downloadBlob(`/modules/certificate?origin=${encodeURIComponent(window.location.origin)}`, "LUMIS-AI-Certificate.pdf");
      toast.success("Certificate downloaded");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not download certificate")); }
    finally { setCertBusy(false); }
  };

  const load = () => api.get("/modules").then((r) => { setModules(r.data.modules); setMeta(r.data); });
  useEffect(() => { load(); }, []);

  const complete = async (id, completed) => {
    try {
      await api.post("/modules/progress", { module_id: id, completed });
      toast.success(completed ? "Module completed" : "Marked incomplete");
      load();
    } catch { toast.error("Could not update"); }
  };

  const done = modules.filter((m) => m.completed).length;
  const pct = modules.length ? Math.round((done / modules.length) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">LUMIS AI™ Academy</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">LUMIS AI™ Academy — Workforce Readiness</h1>
          <p className="text-sm text-slate-600 mt-1">AI in HR Implementation Resources — built on the LUMIS AI–HR Ecosystem™ and the 9-Stage LUMIS AI Across Employee Lifecycle Model™.</p>
          <a href="https://lumisai.co" target="_blank" rel="noreferrer" data-testid="readiness-source-link" className="inline-flex items-center gap-1 text-xs text-[#0A2A5E] hover:underline mt-2">
            Source: LUMIS AI–HR Ecosystem™ · lumisai.co (official LUMIS AI™ website) <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
        <Card data-testid="readiness-resources-placeholder" className="p-4 bg-white border-dashed border-2 border-[#F5A623]/60 max-w-sm">
          <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold">Implementation Resources</div>
          <p className="text-sm text-slate-600 mt-1">Additional courses, implementation resources, and structured learning pathways from the LUMIS AI™ Academy will be connected here.</p>
          <a href="#resources" className="text-xs font-semibold text-[#0A2A5E] hover:underline mt-1 inline-block">Jump to resources ↓</a>
        </Card>
      </div>

      <Card className="p-6 bg-gradient-to-br from-[#0A2A5E] to-[#0F3F8A] text-white border-0 mb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#F5A623] font-bold">Your progress</div>
            <div className="font-display text-4xl font-extrabold mt-1">{done} / {modules.length}</div>
            <div className="text-sm text-slate-300 mt-1">modules completed</div>
          </div>
          <div className="w-64">
            <Progress data-testid="progress-bar" value={pct} className="h-2 bg-[#1E5AB8]"/>
            <div className="text-xs text-slate-400 mt-2 text-right">{pct}% · {meta.passed}/{modules.length} badges</div>
          </div>
        </div>
      </Card>

      {meta.certified && (
        <Card data-testid="certified-banner" className="p-5 mb-6 border-0 bg-gradient-to-r from-[#F5A623] to-[#FBD38D] text-[#0A2A5E] flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center"><Award className="w-7 h-7 text-[#C7761A]" /></div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold">LUMIS AI Certified</div>
            <div className="font-display text-xl font-extrabold">Workforce AI Readiness — all module badges earned</div>
            <div className="text-sm opacity-80">You passed every knowledge check at {meta.pass_mark}% or above. Source: LUMIS AI–HR Ecosystem™.</div>
          </div>
          <Button data-testid="download-certificate-btn" onClick={downloadCertificate} disabled={certBusy} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
            {certBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Download certificate (PDF)
          </Button>
        </Card>
      )}

      <Card data-testid="practice-check-card" className="p-6 mb-6 bg-white border-2 border-[#F5A623]/60 hover-lift">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0A2A5E] flex items-center justify-center flex-shrink-0"><ListChecks className="w-5 h-5 text-[#F5A623]" /></div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold">Learning activity · 5 scenarios · educational only</div>
            <h3 className="font-display font-bold text-lg text-[#0A2A5E] mt-1">LUMIS AI™ Workforce Readiness Practice Check</h3>
            <p className="text-sm text-slate-600 mt-1">Test your understanding of responsible AI use, data protection, workforce transition, bias risk, and human oversight through five practical workplace scenarios.</p>
          </div>
          <Link to="/app/readiness/practice-check"><Button data-testid="practice-check-begin-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Begin Practice Check</Button></Link>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {modules.map((m) => (
          <Card key={m.id} data-testid={`module-${m.id}`} className="p-6 bg-white border-slate-200 hover-lift">
            <div className="flex items-start justify-between gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#FDECD0] flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-5 h-5 text-[#7C3F00]"/>
              </div>
              <div className="flex items-center gap-2">
                {m.quiz_passed && (
                  <span data-testid={`badge-${m.id}`} title={`Badge earned ${m.badge_earned_at?.slice(0, 10)}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gradient-to-r from-[#F5A623] to-[#FBD38D] text-[#0A2A5E]">
                    <Award className="w-3.5 h-3.5" /> Badge · {m.quiz_score}%
                  </span>
                )}
                {m.completed && <CheckCircle2 className="w-5 h-5 text-emerald-600"/>}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold mt-4">{m.stage}</div>
            <h3 className="font-display font-bold text-lg text-[#0A2A5E] mt-1">{m.title}</h3>
            <p className="text-sm text-slate-600 mt-2">{m.description}</p>

            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
              <Clock className="w-3.5 h-3.5"/> {m.duration_minutes} min
              <span className="text-slate-300">|</span>
              <a href={m.source_url} target="_blank" rel="noreferrer" className="hover:underline truncate" data-testid={`module-source-${m.id}`}>Source: {m.source}</a>
              <ListenButton src={`/tts/module/${m.id}`} label="Listen" costHint="~1¢" testid={`module-listen-${m.id}`} />
            </div>

            <ul className="mt-4 space-y-1.5">
              {m.lessons.map((l, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-[#C7761A] font-mono">·</span> {l}
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-2 gap-2 mt-5">
              <Button
                data-testid={`module-quiz-${m.id}`}
                onClick={() => setQuizModule(m)}
                variant="outline"
                className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"
              >
                <Award className="w-4 h-4 mr-2" /> {m.quiz_passed ? "Retake quiz" : `Take quiz (${m.quiz_questions})`}
              </Button>
              <Button
                data-testid={`module-complete-${m.id}`}
                onClick={() => complete(m.id, !m.completed)}
                className={`${m.completed ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#0A2A5E] hover:bg-[#0F3F8A]"} text-white`}
              >
                {m.completed ? "Completed — Undo" : "Mark complete"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <QuizDialog module={quizModule} open={!!quizModule} onOpenChange={(v) => !v && setQuizModule(null)} onDone={load} />

      {user?.role === "manager" && <ReadinessAnalytics />}

      <div id="resources"><ResourceEmbeds /></div>
    </div>
  );
}
